


import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { pdf } from '@react-pdf/renderer';
import { toast } from "react-toastify";
import Swal from 'sweetalert2'; 
import ChatPdfDocument from './ChatPdfDocument';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

// A simple SVG Trash Icon component for reuse
const TrashIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd" />
    </svg>
);

// A simple SVG Spinner Icon for loading states
const SpinnerIcon = () => (
    <svg className="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
);


const UserChatHistory = () => {
    const [usersData, setUsersData] = useState([]);
    console.log("🚀 ~ UserChatHistory ~ usersData:", usersData)
    const [isDownloadingAll, setIsDownloadingAll] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [expandedUser, setExpandedUser] = useState(null);
    const [expandedChat, setExpandedChat] = useState(null);
    const [processingSessionId, setProcessingSessionId] = useState(null);
    const [isDeleting, setIsDeleting] = useState({ id: null, type: null });

    const fetchUsers = useCallback(async (page, limit) => {
        setIsLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const headers = token ? { Authorization: `Bearer ${token}` } : {};

            const response = await axios.get(`${API_BASE_URL}/admin/all-users`, {
                headers: headers,
                params: { page, limit },
            });

            if (response?.data) {
                setUsersData(response.data.data || []);
                setCurrentPage(response.data.page || 1);
                setTotalPages(response.data.totalPages || 0);
                setTotalItems(response.data.totalItems || 0);
                if (response.data.data.length === 0 && response.data.totalItems > 0 && page > 1) {
                    setCurrentPage(prev => Math.max(1, prev - 1));
                }
            } else {
                setUsersData([]); setTotalPages(0); setTotalItems(0);
                setError('No data received from server.');
            }
        } catch (err) {
            console.error('Error fetching user data:', err);
            setError(err.response?.data?.message || err.message || 'An unexpected error occurred');
            setUsersData([]); setTotalPages(0); setTotalItems(0);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchUsers(currentPage, itemsPerPage);
    }, [currentPage, itemsPerPage, fetchUsers]);


    const handleDeleteUser = async (userId, userEmail) => {
        const result = await Swal.fire({
            title: `Delete user ${userEmail}?`,
            text: "This will permanently delete the user and all their chat histories. This action cannot be undone.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete user!',
            cancelButtonText: 'Cancel',
            customClass: {
            confirmButton: 'swal2-confirm-button',
            cancelButton: 'swal2-cancel-button'
        }
        });

        if (result.isConfirmed) {
            setIsDeleting({ id: userId, type: 'user' });
            try {
                const token = localStorage.getItem('token');
                const headers = token ? { Authorization: `Bearer ${token}` } : {};
                await axios.delete(`${API_BASE_URL}/admin/delete-user/${userId}`, { headers });
                
                toast.success('User deleted successfully.');
                fetchUsers(currentPage, itemsPerPage); // Re-fetch to update the list
            } catch (err) {
                console.error('Error deleting user:', err);
                toast.error(err.response?.data?.metadata?.message || 'Failed to delete user.');
            } finally {
                setIsDeleting({ id: null, type: null });
            }
        }
    };

    const handleDeleteSession = async (userId, sessionId) => {
        const result = await Swal.fire({
            title: 'Delete this chat session?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel',
             customClass: {
            confirmButton: 'swal2-confirm-button',
            cancelButton: 'swal2-cancel-button'
        }
        });

        if (result.isConfirmed) {
            setIsDeleting({ id: sessionId, type: 'session' });
            try {
                const token = localStorage.getItem('token');
                const headers = token ? { Authorization: `Bearer ${token}` } : {};
                await axios.delete(`${API_BASE_URL}/admin/${userId}/session/${sessionId}`, { headers });

                toast.success('Chat session deleted successfully.');
                
                // Update state locally for a better UX (no full reload)
                setUsersData(prevUsers => 
                    prevUsers.map(user => {
                        if (user._id === userId) {
                            return {
                                ...user,
                                chats: user.chats.filter(chat => chat.sessionId !== sessionId)
                            };
                        }
                        return user;
                    })
                );
            } catch (err) {
                console.error('Error deleting session:', err);
                toast.error(err.response?.data?.metadata?.message || 'Failed to delete session.');
            } finally {
                setIsDeleting({ id: null, type: null });
            }
        }
    };


const handleDownloadAllPdfs = async () => {
  setIsDownloadingAll(true);
  const toastId = toast.loading("Preparing PDFs for download...");
  
  try {
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    const response = await axios.post(
      `${API_BASE_URL}/admin/download-chat-pdfs`,
      { page: currentPage },
      {
        headers: headers,
        responseType: 'blob' // Important for file download
      }
    );

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `all_chat_pdfs_page_${currentPage}.zip`);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);

    toast.update(toastId, {
      render: `Downloaded PDFs successfully!`,
      type: "success",
      isLoading: false,
      autoClose: 3000
    });
  } catch (error) {
    console.error("Error downloading PDFs:", error);
    toast.update(toastId, {
      render: `Failed to download PDFs: ${error.message}`,
      type: "error",
      isLoading: false,
      autoClose: 5000
    });
  } finally {
    setIsDownloadingAll(false);
  }
};


const handleDeleteAllConversations = async () => {
    const result = await Swal.fire({
        title: 'Are you absolutely sure?',
        text: "You want to delete ALL chat histories for ALL users? This action cannot be undone.",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33', // Dark red
        cancelButtonColor: '#3085d6', // Blue
        confirmButtonText: 'Yes, delete all!',
        cancelButtonText: 'Cancel',
        customClass: {
            confirmButton: 'swal2-confirm-button',
            cancelButton: 'swal2-cancel-button'
        }
    });
    if (result.isConfirmed) {
        setIsDeleting({ id: 'all', type: 'all' });
        try {
            const token = localStorage.getItem('token');
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            await axios.delete(`${API_BASE_URL}/user/delete-all-conversation`, { headers });
            toast.success('All chat histories deleted successfully.');
            fetchUsers(1, itemsPerPage);
            setExpandedUser(null);
            setExpandedChat(null);
        } catch (err) {
            console.error('Error deleting all chat histories:', err);
            toast.error(err.response?.data?.metadata?.message || err.message || 'Failed to delete all chat histories.');
        } finally {
            setIsDeleting({ id: null, type: null });
        }
    }
};

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage && !isLoading) {
          setCurrentPage(newPage);
          setExpandedUser(null);
          setExpandedChat(null);
          setProcessingSessionId(null); 
        }
    };
    
    const handleItemsPerPageChange = (e) => {
        const newLimit = parseInt(e.target.value, 10);
        setItemsPerPage(newLimit);
        setCurrentPage(1);
        setExpandedUser(null);
        setExpandedChat(null);
        setProcessingSessionId(null); 
    };
    
    const toggleUserExpansion = (userId) => {
        setExpandedUser(expandedUser === userId ? null : userId);
        setExpandedChat(null);
        setProcessingSessionId(null); 
    };
    
    const toggleChatExpansion = (sessionId) => {
        setExpandedChat(expandedChat === sessionId ? null : sessionId);
    };
    
    // const handleExportChatPdf = async (chat, guestId, userName) => {
    //     if (!chat || !chat.conversation || chat.conversation.length === 0) {
    //         toast.error("No conversation data available for export.");
    //         return;
    //     }
    
    //     setProcessingSessionId(chat.sessionId); 
    //     const toastId = toast.loading("Preparing chat PDF...", { id: `exportToast-${chat.sessionId}` }); 
    
    //     try {
    //         const pdfBlob = await pdf(<ChatPdfDocument chat={chat} userName={userName} />).toBlob();
    //         // Change toast message to reflect opening in new tab
    //         toast.update(toastId, { render: "Generated PDF. Now opening in new tab and uploading..." });
    
    //         const filename = `chat_session_${chat.sessionId}.pdf`;
    //         const url = URL.createObjectURL(pdfBlob);
    //         const link = document.createElement('a');
    //         link.href = url;
    //         link.target = '_blank'; // Open in a new tab
    //         // REMOVED: link.download = filename; // This line triggers the "Save As" dialog for downloading
    //         document.body.appendChild(link);
    //         link.click();
    //         document.body.removeChild(link);
    //         URL.revokeObjectURL(url);
    
    //         // Update toast message for successful opening
    //         toast.update(toastId, { render: "PDF opened in new tab.", type: "success", isLoading: false, autoClose: 3000 });
    
    //         const formData = new FormData();
    //         formData.append('pdfFile', pdfBlob, filename);
    //         formData.append('sessionId', chat.sessionId);
    //         formData.append('guestId', guestId);
    
    //         const token = localStorage.getItem('token');
    //         const headers = token ? { Authorization: `Bearer ${token}` } : {};
    
    //         const uploadResponse = await axios.post(`${API_BASE_URL}/admin/upload-chat-pdf`, formData, {
    //             headers: { ...headers, 'Content-Type': 'multipart/form-data' },
    //         });
    
    //         if (uploadResponse?.data?.s3Url) {
    //             // toast.success(`PDF also uploaded to S3 successfully!`, { duration: 4000 });
    //             console.log("Uploaded to S3:", uploadResponse.data.s3Url);
    //         } else {
    //             // Adjust warning message as well
    //             toast.warn("PDF opened, but S3 upload finished with no URL.", { duration: 4000 });
    //         }
    
    //     } catch (err) {
    //         console.error('Error during chat export:', err);
    //         toast.error(err.response?.data?.message || err.message || 'Failed to export chat PDF', { id: toastId });
    //     } finally {
    //         setProcessingSessionId(null); 
    //     }
    // };
      

    const handleExportChatPdf = (chat) => {
  if (!chat.pdfS3Url) {
    toast.error("PDF not available for this chat.");
    return;
  }
  
  // Open the PDF in a new tab
  window.open(chat.pdfS3Url, '_blank');
};

    const PaginationControls = () => {
        // ... (No changes needed in this function, it's fine as is)
        if (!totalPages || totalPages <= 0) return null;
    
        const getPageNumbers = () => {
            const pageNumbers = [];
            const maxPagesToShow = 5;
            const halfMaxPages = Math.floor(maxPagesToShow / 2);
    
            if (totalPages <= maxPagesToShow) {
                for (let i = 1; i <= totalPages; i++) { pageNumbers.push(i); }
            } else {
                let startPage = Math.max(1, currentPage - halfMaxPages);
                let endPage = startPage + maxPagesToShow - 1;
    
                if (endPage > totalPages) {
                    endPage = totalPages;
                    startPage = endPage - maxPagesToShow + 1;
                }
    
                if (startPage > 1) {
                    pageNumbers.push(1);
                    if (startPage > 2) { pageNumbers.push('...'); }
                }
                for (let i = startPage; i <= endPage; i++) { pageNumbers.push(i); }
                if (endPage < totalPages) {
                    if (endPage < totalPages - 1) { pageNumbers.push('...'); }
                    pageNumbers.push(totalPages);
                }
            }
            return pageNumbers;
        };
        
        const pageDisplayInfo = totalItems > 0 ? `Page ${currentPage} of ${totalPages} (Total: ${totalItems} users)` : `Page ${currentPage} of ${totalPages}`;
    
        return (
            <div className="mt-6 flex flex-col sm:flex-row justify-between items-center bg-white px-4 py-3 border-t border-gray-200 rounded-b-lg">
                 <div className="text-sm text-gray-700 mb-2 sm:mb-0 flex items-center space-x-4">
                    <span>{pageDisplayInfo}</span>
                    <div className="flex items-center space-x-2">
                        <span className="sr-only sm:not-sr-only">Items per page:</span>
                        <select 
                          value={itemsPerPage} 
                          onChange={handleItemsPerPageChange}
                          className="px-2 py-1 border border-gray-300 rounded-md bg-white text-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value={5}>5</option>
                          <option value={10}>10</option>
                          <option value={20}>20</option>
                          <option value={50}>50</option>
                        </select>
                    </div>
                </div>
                {totalPages > 1 && (
                    <nav className="inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                        <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1 || isLoading} className={`relative inline-flex items-center px-3 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed`}>Previous</button>
                        {getPageNumbers().map((page, index) => (
                            typeof page === 'number' ? (
                                <button key={index} onClick={() => handlePageChange(page)} disabled={isLoading} className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium ${currentPage === page ? 'z-10 bg-blue-50 border-blue-500 text-blue-600' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>{page}</button>
                            ) : (
                                <span key={index} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">{page}</span>
                            )
                        ))}
                        <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages || isLoading} className={`relative inline-flex items-center px-3 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed`}>Next</button>
                    </nav>
                )}
            </div>
        );
    };

    return (
                <div className="p-4 md:p-6 bg-gray-50 min-h-full">
            <div className="">
                <div className="flex flex-col sm:flex-row justify-between items-center mb-6">
                    <h1 className="text-2xl md:text-3xl font-semibold text-gray-800 mb-3 sm:mb-0 ">
                        User Chat History
                    </h1>

                    <div className="flex space-x-2">
                        <button
                            onClick={handleDownloadAllPdfs}
                            disabled={isDeleting.type === 'all' || totalItems === 0} 
                            className={`px-4 py-2 rounded-md shadow-sm font-medium text-white transition-colors duration-200
                                ${isDeleting.type === 'all' || totalItems === 0
                                    ? 'bg-blue-300 cursor-not-allowed'
                                    : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                                }`}
                        >
                            {isDownloadingAll ? <SpinnerIcon /> : 'Download All Chat History'}
                        </button>

                        <button
                            onClick={handleDeleteAllConversations}
                            disabled={isDeleting.type === 'all' || totalItems === 0} 
                            className={`px-4 py-2 rounded-md shadow-sm font-medium text-white transition-colors duration-200
                                ${isDeleting.type === 'all' || totalItems === 0
                                    ? 'bg-red-300 cursor-not-allowed'
                                    : 'bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2'
                                }`}
                        >
                            {isDeleting.type === 'all' ? <SpinnerIcon /> : 'Delete All Chats'}
                        </button>
                    </div>
                </div>

                {error && <div className="mb-4 p-3 bg-red-100 text-red-700 border border-red-300 rounded-md shadow-sm">{error}</div>}

                <div className="bg-white shadow-md rounded-lg overflow-hidden">
                    {isLoading && usersData.length === 0 ? (
                        <div className="p-10 text-center text-gray-500 flex flex-col items-center justify-center">
                           <SpinnerIcon />
                           <p className="mt-2">Loading user chat history...</p>
                        </div>
                    ) : !isLoading && usersData.length === 0 ? (
                        <div className="p-10 text-center text-gray-500">
                           <h3 className="text-lg font-medium">No Users Found</h3>
                           <p className="text-sm">There are no user chat records to display.</p>
                        </div>
                    ) : (
                        <>
                            <div className="user-list space-y-3 p-4">
                                {usersData.map((user) => (
                                    <div key={user._id} className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                                        <div
                                            className="p-3 flex justify-between items-center bg-gray-100 cursor-pointer font-medium text-gray-700 hover:bg-gray-200 transition-colors duration-200"
                                            onClick={() => toggleUserExpansion(user._id)}
                                        >
                                            <div className="flex items-center">
                                                <span className="text-base md:text-lg font-medium">{user.name || 'N/A'}</span>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteUser(user._id, user.name);
                                                    }}
                                                    disabled={isDeleting.id === user._id}
                                                    className="p-2 rounded-full text-red-500 hover:bg-red-100 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                                                    title="Delete this user"
                                                >
                                                    {isDeleting.id === user._id ? <SpinnerIcon /> : <TrashIcon />}
                                                </button>
                                                <span className="text-sm text-gray-500 w-4 text-center">{expandedUser === user._id ? '▲' : '▼'}</span>
                                            </div>
                                        </div>

                                        {expandedUser === user._id && (
                                            <div className="p-4 border-t border-gray-200">
                                                {user.chats && user.chats.length > 0 ? (
                                                    <div className="space-y-3">
                                                        {user.chats.map((chat) => (
                                                            <div key={chat.sessionId} className="bg-gray-50 border border-gray-200 rounded-md overflow-hidden">
                                                                <div className="p-3 flex justify-between items-center bg-gray-100 font-medium text-gray-700 text-base">
                                                                    <div className="flex-grow flex items-center cursor-pointer hover:bg-gray-200 p-1 rounded" onClick={() => toggleChatExpansion(chat.sessionId)}>
                                                                        <span>Session ID: <span className="font-normal text-gray-600 text-sm">{chat.sessionId}</span></span>
                                                                        <span className="text-xs ml-2 text-gray-500 w-4 text-center">{expandedChat === chat.sessionId ? '▲' : '▼'}</span>
                                                                    </div>
                                                                    <div className="flex items-center space-x-2 ml-4">
                                                                       {/* <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                // Passed user.email as the username for PDF.
                                                                                handleExportChatPdf(chat, user.guestId, user.name);
                                                                            }}
                                                                            disabled={processingSessionId !== null} 
                                                                            className={`ml-4 px-3 py-1 text-xs rounded-md shadow focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200 ${processingSessionId !== null ? 'bg-blue-300 text-gray-700 cursor-not-allowed' : 'bg-blue-500 text-white hover:bg-blue-600'}`}
                                                                            title="Generate PDF, Open in New Tab, and Upload to S3" 
                                                                        >
                                                                            {processingSessionId === chat.sessionId ? 'Processing...' : 'Download'}
                                                                        </button> */}


                                                                        <button
  onClick={(e) => {
    e.stopPropagation();
    handleExportChatPdf(chat);
  }}
  className="ml-4 px-3 py-1 text-xs rounded-md shadow focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200 bg-blue-500 text-white hover:bg-blue-600"
  title="Open PDF in new tab"
>
  Download
</button>
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleDeleteSession(user._id, chat.sessionId);
                                                                            }}
                                                                            disabled={isDeleting.id === chat.sessionId}
                                                                            className="p-2 rounded-full text-red-500 hover:bg-red-100 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                                                                            title="Delete this session"
                                                                        >
                                                                            {isDeleting.id === chat.sessionId ? <SpinnerIcon /> : <TrashIcon />}
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                                {expandedChat === chat.sessionId && (
                                                                    <div className="p-3 border-t border-dashed border-gray-300">
                                                                        {chat.conversation && chat.conversation.length > 0 ? (
                                                                        <div className="space-y-3">
                                                                            {chat.conversation.map((msg, msgIndex) => (
                                                                                <div key={msg._id || msgIndex} className="p-3 rounded-md text-sm leading-relaxed">
                                                                                    <p className="text-gray-700 font-semibold mb-1 border-l-4 border-blue-500 pl-2">System: <span className="font-normal">{msg.system}</span></p>
                                                                                    <p className="text-gray-600 border-l-4 border-gray-400 pl-2">User: <span className="italic">{msg.user}</span></p>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    ) : (
                                                                        <p className="text-gray-500 italic text-center text-sm py-2">No conversation in this session.</p>
                                                                    )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <p className="text-gray-500 italic text-center text-sm py-2">No chat sessions for this user.</p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                            <PaginationControls />
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default UserChatHistory;